// src/components/bucket/Activity/AddActivity.jsx
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  SimpleGrid,
  GridItem,
  Select,
  Textarea,
} from "@chakra-ui/react";
import axiosInstance from "../../../helpers/axiosInstance";
import currencyFormatter from "currency-formatter";
import Translate from "../../../components/LanguageTranslation/Translate";

const capitalizeWords = (str) =>
  str.replace(/\b\w/g, (char) => char.toUpperCase());

const AddActivity = ({
  isOpen,
  onClose,
  addTripItem,
  defaultSubcategories = [],
  editItem = null,
  onEditSuccess = null,
}) => {
  // Common fields
  const [title, setTitle] = useState("");
  const [webURL, setWebURL] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        setTitle(editItem.title || "");
        setWebURL(editItem.webURL || editItem.productUrl || "");
      } else {
        setTitle("");
        setWebURL("");
      }
      setErrors({});
    }
  }, [isOpen, editItem]);

  const normalizedURL = (url) =>
    url.startsWith("http") ? url : `https://${url}`;
  const isValidURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleCreate = async () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "Title is required.";
    if (!webURL.trim() || !isValidURL(normalizedURL(webURL)))
      newErrors.webURL = "Valid URL required.";

    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    // Only normal 3rd-party Viator payload
    const payload = {
      title,
      productUrl: normalizedURL(webURL),
      preference: defaultSubcategories[0],
    };

    // Add item_id to payload if editing
    if (editItem && editItem._id) {
      payload.item_id = editItem._id;
    }

    try {
      const res = await axiosInstance.post(
        "/bucket-list/add-manual-explore",
        payload
      );
      if (res.data.success) {
        if (editItem && editItem._id) {
          // Update existing item
          const updatedItem = {
            ...editItem,
            title,
            productUrl: normalizedURL(webURL),
          };
          if (onEditSuccess) {
            onEditSuccess(updatedItem);
          }
        } else {
          // Add new item
          const newItem = {
            ...payload,
            _id: res.data.data._id,
            category: "explore",
            data_saved: "manual",
          };
          addTripItem(newItem);
        }
      }
    } catch (err) {
      console.error("Error saving activity:", err);
    }

    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxH="80vh" overflowY="auto">
        <ModalHeader>
          <Translate
            text={
              editItem
                ? "Edit Activity"
                : `Add ${defaultSubcategories[0] ? capitalizeWords(defaultSubcategories[0]) : "Activity"}`
            }
          />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={2} spacing={4}>
            {/* Title */}
            <GridItem colSpan={2}>
              <FormControl isInvalid={errors.title}>
                <FormLabel>
                  {" "}
                  <Translate text="Title" />
                </FormLabel>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.title} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            {/* Website URL */}
            <GridItem colSpan={2}>
              <FormControl isInvalid={errors.webURL}>
                <FormLabel>
                  {" "}
                  <Translate text="Booking Link" />
                </FormLabel>
                <Input
                  value={webURL}
                  onChange={(e) => setWebURL(e.target.value)}
                />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.webURL} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="customGreen" mr={3} onClick={handleCreate}>
            <Translate text="Save" />
          </Button>
          <Button onClick={onClose}>
            {" "}
            <Translate text="Cancel" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddActivity;
